/*
 * tc-card-swatch-select.js: product-card colour swatches select with a real image
 * for keyboard users (issue #389, assembled-QA finding D1, WCAG 2.1.1).
 *
 * Additive and isolated, same pattern as atc-guard.js: it does NOT modify
 * assets/theme.js (a minified bundle with no source in this repo). It only sets
 * an attribute theme.js already observes, and re-dispatches a click theme.js
 * already handles.
 *
 * HOW THE CARD SWAPS IMAGES (read from assets/theme.js):
 * - snippets/product-grid-item.liquid renders one <product-grid-item-variant>
 *   panel per colour; all but the first carry `hidden`, and every image a panel
 *   does not show at load is wrapped in <template> inside its
 *   <product-grid-item-image> (snippets/product-grid-item-variant.liquid), so it
 *   costs no request.
 * - When a hidden panel upgrades, ProductGridItemVariant.attributeChangedCallback
 *   sees `hidden` arrive (old value null) and runs showVariant(), which moves
 *   data-grid-current-image from the first image to the colour's own image. The
 *   first image's wrapper loses .is-active (theme.css .product-grid-item__image-
 *   wrapper: visibility hidden), and the colour image's wrapper gains it while
 *   its content is STILL an unstamped <template>.
 * - The template is only stamped by ProductGridItemImage.eagerLoad(), which runs
 *   when the element's `loading` attribute becomes "eager". The ONLY caller that
 *   does that for a hidden panel is ProductGridItem.swatchHoverEvent, bound to
 *   `mouseenter` on the swatch link.
 * - Selection itself is ProductGridItem.swatchClickEvent, bound to `click`: it
 *   moves aria-current and un-hides the chosen panel.
 * So a mouse (or a touch tap, which fires compatibility mouse events first)
 * hydrates the panel before it is shown, and a keyboard Enter, which fires only
 * `click`, shows a panel whose visible slot is an empty <template>: a blank card.
 * Before #389 the dot row was invisible at rest, which is why the path went
 * unnoticed.
 *
 * FIX, on the same events selection uses:
 * 1. focusin on a swatch link hydrates its panel (the keyboard counterpart of
 *    the mouseenter preload), so the image is usually ready before Enter.
 * 2. click, capture phase on document (runs before theme.js's listener on the
 *    link): hydrate the target panel; if its current image has not finished
 *    loading, hold the click (the old image stays on screen) and replay it on
 *    the image's load/error (an image that already failed counts as ready),
 *    or after 1500ms at the latest so selection can never get stuck. The
 *    replay is an ordinary click on the same link, which theme.js handles
 *    exactly as before; focus never moves.
 * Mouse and touch go through the same code and gain the same no-blank guarantee.
 *
 * ROW ARROWS FOR MOUSE-WHEEL USERS (assembled-QA note N3, measured 2026-09-28).
 * The dot row (.grid__swatch__hover) is overflow-x:auto with its scrollbar
 * pushed out of view, so touch swipe and trackpad / shift-wheel already reach
 * every dot. A plain vertical wheel does not, and the card's own prev/next
 * .tabs__arrow buttons are the theme's answer: theme.js wires them through
 * NativeScrollbar only where a section registers its `siblings` module (the
 * collection section does; the homepage kit bands such as tc-product-grid do
 * not), so outside those sections both arrows keep `is-hidden` forever.
 * On pointer entry to a card, and only after OWNERSHIP_SETTLE_MS, if the row
 * overflows and BOTH arrows still carry is-hidden, this wires them the same
 * way: toggle is-hidden from the scroll position and scroll the row on click.
 * Why the wait (Codex review of fec10e1): NativeScrollbar binds its listeners
 * at construction but only reveals an arrow in a setTimeout 200ms later, so
 * during those 200ms a theme-owned row looks exactly like an unowned one and
 * an immediate check could bind a second pair of listeners. After 250ms an
 * overflowing theme-owned row always shows at least one arrow (its start or
 * end rule un-hides one), so the re-check is decisive. The wait is measured
 * from pointer entry, and theme.js constructs NativeScrollbar synchronously
 * when it initialises or re-renders a section, which is always before any
 * pointer can enter that new markup. A row judged theme-owned is marked
 * data-tc-row-arrows="theme" and never re-checked; rows wired here are marked
 * "tc". Rows the theme already manages are left alone, so nothing is bound
 * twice. Right-to-left
 * rows use NativeScrollbar's endpoint rules (scrollLeft 0 at the start, negative
 * toward the end); the scroll step is physical, so it needs no sign flip. Wired
 * rows resync on theme:resize, and hide both arrows once they stop overflowing.
 *
 * Only the latest held selection per card can replay: a newer click on any
 * swatch in that card cancels the older hold (listeners and timer).
 */
(function () {
  'use strict';
  if (window.__tcCardSwatchSelect) return;
  window.__tcCardSwatchSelect = true;

  var SWATCH = 'product-grid-item a.swatch__label[data-grid-item-variant]';
  var HOLD_MS = 1500;
  var replaying = null;

  function swatchFrom(target) {
    return target && target.closest ? target.closest(SWATCH) : null;
  }

  function panelFor(link) {
    var card = link.closest('product-grid-item');
    var id = link.getAttribute('data-grid-item-variant');
    if (!card || !id) return null;
    return card.querySelector('product-grid-item-variant[data-grid-item-variant="' + id + '"]');
  }

  // What swatchHoverEvent does on mouseenter: ProductGridItemImage observes
  // `loading` and stamps its <template> when it becomes "eager". eagerLoad()
  // only promotes an <img> that is the element's FIRST child, and the image
  // snippet can wrap it, so any <img> still lazy inside the (display:none)
  // panel is promoted here too; a lazy image in a hidden panel never loads.
  function hydrate(panel) {
    var images = panel.querySelectorAll('product-grid-item-image');
    for (var i = 0; i < images.length; i++) {
      if (images[i].getAttribute('loading') !== 'eager') images[i].setAttribute('loading', 'eager');
    }
    var imgs = panel.querySelectorAll('img[loading="lazy"]');
    for (var j = 0; j < imgs.length; j++) imgs[j].setAttribute('loading', 'eager');
  }

  function currentImage(panel) {
    var slot = panel.querySelector('product-grid-item-image[data-grid-current-image]');
    return slot ? slot.querySelector('img') : null;
  }

  // complete is true once the image has loaded OR failed; a failed image will
  // never fire load, so it must not hold the selection either.
  function isReady(img) {
    return !img || img.complete;
  }

  document.addEventListener('focusin', function (event) {
    var link = swatchFrom(event.target);
    var panel = link && panelFor(link);
    if (panel) hydrate(panel);
  });

  // One pending (held) selection per card. Any newer selection in the same
  // card, including a re-click of the held swatch or of the one on screen,
  // cancels the older hold's load/error listeners and its timer first, so a
  // late image can never replay a stale click and revert the selection
  // (Codex review P2 on PR #394).
  var pending = typeof WeakMap === 'function' ? new WeakMap() : null;

  function cancelPending(card) {
    if (!pending || !card) return;
    var cancel = pending.get(card);
    if (cancel) {
      pending.delete(card);
      cancel();
    }
  }

  document.addEventListener('click', function (event) {
    var link = swatchFrom(event.target);
    if (!link || link === replaying) return;
    var card = link.closest('product-grid-item');
    cancelPending(card);
    var panel = panelFor(link);
    if (!panel || !panel.hasAttribute('hidden')) return;
    hydrate(panel);
    var img = currentImage(panel);
    if (isReady(img)) return;

    event.preventDefault();
    event.stopPropagation();
    var timer = null;
    var cleanup = function () {
      img.removeEventListener('load', replay);
      img.removeEventListener('error', replay);
      window.clearTimeout(timer);
    };
    var replay = function () {
      cleanup();
      if (pending && pending.get(card) === cleanup) pending.delete(card);
      replaying = link;
      try { link.click(); } finally { replaying = null; }
    };
    img.addEventListener('load', replay);
    img.addEventListener('error', replay);
    timer = window.setTimeout(replay, HOLD_MS);
    if (pending && card) pending.set(card, cleanup);
  }, true);

  var OWNERSHIP_SETTLE_MS = 250;

  function wireArrows(container) {
    if (container.hasAttribute('data-tc-row-arrows')) return;
    var row = container.querySelector('.grid__swatch__hover');
    var prev = container.querySelector('.tabs__arrow--prev');
    var next = container.querySelector('.tabs__arrow--next');
    if (!row || !prev || !next) return;
    if (!prev.classList.contains('is-hidden') || !next.classList.contains('is-hidden')) {
      container.setAttribute('data-tc-row-arrows', 'theme');
      return;
    }
    if (row.scrollWidth <= row.clientWidth + 1) return;
    container.setAttribute('data-tc-row-arrows', 'tc');
    // Arrows are physical, as in theme.js NativeScrollbar: next (right edge)
    // scrolls right, prev (left edge) scrolls left, so the scroll offset sign
    // is the same in both directions. Only the endpoints differ: in RTL the
    // row starts at scrollLeft 0 (its right end) and runs NEGATIVE to the
    // left (Codex review P2 on PR #394). The sign of scrollLeft follows the
    // scroll container's own direction, so that is what is read, on every
    // sync; with <html dir="rtl"> (window.isRTL in layout/theme.liquid) the
    // row inherits rtl and the two agree.
    // A row that no longer overflows (after a resize) hides both arrows.
    var sync = function () {
      var max = row.scrollWidth - row.clientWidth;
      if (max <= 1) {
        prev.classList.add('is-hidden');
        next.classList.add('is-hidden');
      } else if (window.getComputedStyle(row).direction === 'rtl') {
        prev.classList.toggle('is-hidden', Math.abs(row.scrollLeft) >= max - 1);
        next.classList.toggle('is-hidden', row.scrollLeft >= -1);
      } else {
        prev.classList.toggle('is-hidden', row.scrollLeft <= 1);
        next.classList.toggle('is-hidden', row.scrollLeft + row.clientWidth >= row.scrollWidth - 1);
      }
    };
    var step = function (direction) {
      return function (event) {
        event.preventDefault();
        row.scrollBy({ left: direction * Math.max(34, row.clientWidth - 34), behavior: 'smooth' });
      };
    };
    row.addEventListener('scroll', sync, { passive: true });
    prev.addEventListener('click', step(-1));
    next.addEventListener('click', step(1));
    wiredRows.push(sync);
    sync();
  }

  // Scroll alone does not cover a resize: a row parked at its end can start
  // (or stop) overflowing without scrolling, leaving a stale arrow (Codex
  // review on d964e83). Resync every wired row on theme.js's own
  // `theme:resize` (debounced, dispatched on document, the event
  // NativeScrollbar listens to). A debounced window resize is the fallback
  // and stands down as soon as theme:resize has been seen.
  var wiredRows = [];
  var sawThemeResize = false;
  var resizeTimer = null;
  function syncAll() {
    for (var i = 0; i < wiredRows.length; i++) wiredRows[i]();
  }
  document.addEventListener('theme:resize', function () {
    sawThemeResize = true;
    syncAll();
  });
  window.addEventListener('resize', function () {
    if (sawThemeResize) return;
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      if (!sawThemeResize) syncAll();
    }, 250);
  });

  document.addEventListener('pointerover', function (event) {
    var card = event.target && event.target.closest ? event.target.closest('product-grid-item') : null;
    var container = card && card.querySelector('.product__grid__info > .grid__swatch__container');
    if (!container || container.hasAttribute('data-tc-row-arrows') || container.hasAttribute('data-tc-row-arrows-pending')) return;
    container.setAttribute('data-tc-row-arrows-pending', '');
    window.setTimeout(function () {
      container.removeAttribute('data-tc-row-arrows-pending');
      if (container.isConnected) wireArrows(container);
    }, OWNERSHIP_SETTLE_MS);
  });

  // FOCUS-RING BAND (issue #705, PR #706). custom-css.css (#705 block) opens a
  // 6px clip band left of a text-left dot row while its first dot has keyboard
  // focus, so that dot's focus ring paints whole. If the row then scrolls while
  // the dot keeps focus (ArrowRight, wheel, trackpad), the open band would show
  // a sliver of a scrolled-out dot left of the text edge (independent QA, round
  // 2). Mark a row that is not at its start, and the CSS keeps the band shut.
  // Focusing the first dot scrolls the row back to its start, which clears the
  // mark, so the ring is whole exactly when it is needed. scroll does not
  // bubble, so this listens in the capture phase; it is delegated, so rows that
  // theme.js re-renders need no re-binding. Math.abs covers RTL rows, which run
  // negative from 0.
  document.addEventListener('scroll', function (event) {
    var row = event.target;
    if (!row || !row.classList || !row.classList.contains('grid__swatch__hover')) return;
    var container = row.parentElement;
    if (!container || !container.classList.contains('grid__swatch__container')) return;
    container.classList.toggle('tc-row-scrolled', Math.abs(row.scrollLeft) > 0.5);
  }, { capture: true, passive: true });

  // ARROWS AT A ROW END KEEP FOCUS (issue #714, a11y-qa F2 on PR #706, WCAG
  // 2.4.3). An arrow gets is-hidden when its row reaches that end: theme.js
  // NativeScrollbar does it 200ms after each scroll on collection pages, and
  // wireArrows above does it on kit bands. In a focused card theme.css makes
  // .is-hidden visibility: hidden, so an arrow pressed to the end by keyboard
  // vanished under focus and focus fell to <body>. custom-css.css (#714 block)
  // now keeps a keyboard-focused arrow visible while it is is-hidden. Here that
  // state is exposed as aria-disabled="true" (a screen reader says "unavailable"),
  // and a press on it does nothing, the house pattern of #654 for carousel
  // buttons. Only the focused arrow is watched: a MutationObserver follows its
  // class while it has focus, and on blur it is released, so an arrow that is
  // hidden again carries no stale state. Older browsers without
  // MutationObserver still get the state set on focus.
  var ARROW = '.product__grid__info > .grid__swatch__container > .tabs__arrow';
  var heldArrow = null;
  var heldObserver = null;

  function arrowFrom(target) {
    return target && target.closest ? target.closest(ARROW) : null;
  }

  function syncEndState(arrow) {
    if (arrow.classList.contains('is-hidden')) arrow.setAttribute('aria-disabled', 'true');
    else arrow.removeAttribute('aria-disabled');
  }

  function releaseArrow() {
    if (heldObserver) heldObserver.disconnect();
    if (heldArrow) heldArrow.removeAttribute('aria-disabled');
    heldObserver = null;
    heldArrow = null;
  }

  document.addEventListener('focusin', function (event) {
    var arrow = arrowFrom(event.target);
    if (arrow === heldArrow) return;
    releaseArrow();
    if (!arrow) return;
    heldArrow = arrow;
    syncEndState(arrow);
    if (typeof MutationObserver === 'function') {
      heldObserver = new MutationObserver(function () { syncEndState(arrow); });
      heldObserver.observe(arrow, { attributes: true, attributeFilter: ['class'] });
    }
  });

  document.addEventListener('focusout', function (event) {
    if (heldArrow && event.target === heldArrow) releaseArrow();
  });

  // Capture phase on document runs before the arrow's own click listeners
  // (NativeScrollbar's goToNext/goToPrev, wireArrows' step), so an end-state
  // press, by Enter, Space or pointer, never reaches them.
  document.addEventListener('click', function (event) {
    var arrow = arrowFrom(event.target);
    if (!arrow || arrow.getAttribute('aria-disabled') !== 'true') return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  // A FOCUSED DOT CLEARS THE ROW'S ARROWS (issue #665, WCAG 2.2 SC 2.4.7 and
  // 2.4.11). A card's dot row is a horizontal scroller with its 26px Previous
  // and Next arrows laid over its two ends (theme.css .tabs__arrow). The
  // browser's own focus scroll moves the row only when the focused dot is
  // completely outside it, and it knows nothing about the arrows, so a dot
  // Tabbed to at the row's edge sat partly or wholly under an arrow with its
  // ring cut off. Measured on live 2026-11-02 at 375 on
  // /collections/luggage-suitcases: 8 of 32 Shift+Tab stops fully hidden and 7
  // of 29 Tab stops partly hidden; at 1366 one fully and one partly hidden dot
  // per card in each direction; the same on the 404 carousel at 1024.
  // On keyboard focus of a dot (:focus-visible; mouse and touch focus are left
  // alone) the row scrolls by the smallest amount that puts the dot and its 6px
  // ring between the two arrows. The arrows are measured where they sit (their
  // boxes exist while hidden), so the band is the same whether an arrow shows or
  // not. At either end of the row the scroll stops short, and there theme.js
  // NativeScrollbar (or wireArrows above) hides that end's arrow, so the dot is
  // clear there too. The scroll is instant, like the browser's own focus
  // scroll, so the dot moves once. Chrome scrolls the row before focusin fires;
  // an engine that scrolls after it is caught by a second check on the next
  // frame.
  // Selecting a dot with Enter also scrolls the row: theme.js
  // (NativeScrollbar.scrollToVisibleElement) puts the chosen dot one dot-width
  // from the start, which left about a third of it under the Previous arrow.
  // So after Enter on a dot, the dot is checked again once the row stops
  // scrolling (150ms quiet), or after 2.5s at the latest. The quiet period
  // starts only when the row actually moves after the selection click has
  // reached the document (its bubble phase). The click hold above stops a
  // click whose image is not ready and replays it up to 1.5s later, so a
  // scroll before that, such as this code's own focus scroll landing just
  // after a fast Enter, must not end the wait early (Codex review on #665).
  // And theme.js's smooth scroll can draw its first frame more than 150ms after
  // the click, so the click alone does not start the quiet period either
  // (independent QA on #665: 2 of 324 selections in Chrome). A scroll event
  // that leaves the row where it was at the click does not count.
  // Keys that scroll the row (arrow keys, Home, End, Page Up and Page Down), a
  // pointer press or a wheel turn cancel the check, so it never fights someone
  // scrolling the row. Other keys do not: after a modifier key, or a Tab or
  // Shift+Tab to a neighboring dot, the theme's selection scroll can still be
  // to come (autoreview on #665). So the dot checked when the row settles is
  // whichever dot of that row has keyboard focus then.
  // Out of scope, as in #705 and #714: right-to-left rows (the store is LTR
  // only) and the sibling-product slider (.product__grid__sibs).
  var ROW_DOT = '.product__grid__info > .grid__swatch__container > .grid__swatch__hover a.swatch__label';
  var DOT_RING = 6; // the two-tone ring is drawn 6px outside the dot (#705 block in custom-css.css)
  var SELECT_SETTLE_MS = 150;
  var SELECT_WAIT_MS = 2500;
  var ROW_SCROLL_KEYS = /^(ArrowLeft|ArrowRight|Left|Right|Home|End|PageUp|PageDown)$/;

  function rowDotFrom(target) {
    return target && target.closest ? target.closest(ROW_DOT) : null;
  }

  function keyboardFocused(el) {
    try { return el.matches(':focus-visible'); } catch (e) { return false; }
  }

  // The `side` edge of the container's own arrow of class `cls`, or `fallback`
  // (the container's edge) when the row has no such arrow.
  function arrowEdge(container, cls, side, fallback) {
    for (var i = 0; i < container.children.length; i++) {
      var kid = container.children[i];
      if (!kid.classList.contains(cls)) continue;
      var r = kid.getBoundingClientRect();
      return r.width > 0 ? r[side] : fallback;
    }
    return fallback;
  }

  function revealDot(dot) {
    var row = dot.closest('.grid__swatch__hover');
    var container = row && row.parentElement;
    if (!container) return;
    var max = row.scrollWidth - row.clientWidth;
    if (max <= 1 || window.getComputedStyle(row).direction === 'rtl') return;
    var cbox = container.getBoundingClientRect();
    var origin = row.getBoundingClientRect().left + row.clientLeft; // left edge of the row's visible part
    var lo = Math.max(0, arrowEdge(container, 'tabs__arrow--prev', 'right', cbox.left) - origin) + DOT_RING;
    var hi = Math.min(row.clientWidth, arrowEdge(container, 'tabs__arrow--next', 'left', cbox.right) - origin) - DOT_RING;
    var d = dot.getBoundingClientRect();
    var delta = 0;
    if (d.right - origin > hi) delta = d.right - origin - hi;
    else if (d.left - origin < lo) delta = d.left - origin - lo;
    if (Math.abs(delta) < 0.5) return;
    var target = delta > 0 ? Math.ceil(row.scrollLeft + delta) : Math.floor(row.scrollLeft + delta);
    target = Math.max(0, Math.min(max, target));
    if (Math.abs(target - row.scrollLeft) < 0.5) return;
    var inline = row.style.scrollBehavior;
    row.style.scrollBehavior = 'auto';
    row.scrollLeft = target;
    row.style.scrollBehavior = inline;
    // This scroll is not the theme's selection scroll that a pending check
    // after Enter waits for: move that check's starting point with it, so the
    // scroll event it fires does not start the quiet period (autoreview on
    // #665: Enter, then a quick Shift+Tab whose reveal scrolled the row).
    if (selecting && selecting.row === row) selecting.from = row.scrollLeft;
  }

  function revealIfFocused(dot) {
    if (document.activeElement !== dot || !keyboardFocused(dot)) return;
    try { revealDot(dot); } catch (e) { /* never break the page */ }
  }

  document.addEventListener('focusin', function (event) {
    var dot = rowDotFrom(event.target);
    if (!dot) return;
    revealIfFocused(dot);
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(function () { revealIfFocused(dot); });
    }
  });

  // { dot (the one Enter selected), row, clicked, from, settle, limit }
  var selecting = null;

  function stopSelecting() {
    if (!selecting) return;
    window.clearTimeout(selecting.settle);
    window.clearTimeout(selecting.limit);
    selecting = null;
  }

  function selectionSettled() {
    var row = selecting && selecting.row;
    stopSelecting();
    var dot = rowDotFrom(document.activeElement);
    if (row && dot && dot.closest('.grid__swatch__hover') === row) revealIfFocused(dot);
  }

  document.addEventListener('keydown', function (event) {
    var dot = event.key === 'Enter' ? rowDotFrom(event.target) : null;
    if (dot) {
      stopSelecting();
      selecting = { dot: dot, row: dot.closest('.grid__swatch__hover'), clicked: false, from: 0, settle: null, limit: window.setTimeout(selectionSettled, SELECT_WAIT_MS) };
    } else if (ROW_SCROLL_KEYS.test(event.key)) {
      stopSelecting();
    }
  }, true);
  document.addEventListener('pointerdown', stopSelecting, true);
  document.addEventListener('wheel', stopSelecting, { capture: true, passive: true });

  // Bubble phase: a held click never gets here; its replay does.
  document.addEventListener('click', function (event) {
    if (!selecting || rowDotFrom(event.target) !== selecting.dot) return;
    selecting.clicked = true;
    selecting.from = selecting.row.scrollLeft;
  });
  document.addEventListener('scroll', function (event) {
    if (!selecting || !selecting.clicked || event.target !== selecting.row) return;
    if (Math.abs(selecting.row.scrollLeft - selecting.from) < 0.5) return;
    window.clearTimeout(selecting.settle);
    selecting.settle = window.setTimeout(selectionSettled, SELECT_SETTLE_MS);
  }, { capture: true, passive: true });
})();
